import { useState, useEffect } from 'react';
import { FileText, Edit2, Save, Plus, Trash2, ArrowLeft, Copy, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const FELDTYPEN = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'select', label: 'Auswahl' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Datum' },
  { value: 'textarea', label: 'Textbereich' }
];

const VorlagenVerwaltung = () => {
  const [vorlagen, setVorlagen] = useState([]);
  const [selectedVorlage, setSelectedVorlage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showKopieDialog, setShowKopieDialog] = useState(false);
  const [kopieData, setKopieData] = useState({
    name: '',
    anlagentyp: '',
    beschreibung: ''
  });
  const [selectedVorlageForCopy, setSelectedVorlageForCopy] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadVorlagen();
  }, []);

  const loadVorlagen = async () => {
    try {
      const response = await axios.get(`${API}/vorlagen`, {
        headers: getAuthHeaders()
      });
      setVorlagen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      if (error.response?.status === 403) {
        alert('Nur Administratoren können auf diese Seite zugreifen');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVorlage = (vorlage) => {
    setSelectedVorlage(JSON.parse(JSON.stringify(vorlage)));
    setEditMode(false);
  };

  const handleSaveVorlage = async () => {
    try {
      await axios.put(
        `${API}/vorlagen/${selectedVorlage.id}`,
        {
          name: selectedVorlage.name,
          beschreibung: selectedVorlage.beschreibung,
          pruefpunkte: selectedVorlage.pruefpunkte
        },
        { headers: getAuthHeaders() }
      );
      alert('Vorlage gespeichert');
      setEditMode(false);
      loadVorlagen();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern');
    }
  };

  const handleAddPunkt = (kategorieIndex) => {
    const newPunkt = {
      name: 'Neuer Prüfpunkt',
      typ: 'text',
      pflicht: false,
      optionen: [],
      einheit: null,
      hinweis: null
    };
    const updated = { ...selectedVorlage };
    updated.pruefpunkte[kategorieIndex].punkte.push(newPunkt);
    setSelectedVorlage(updated);
  };

  const handleRemovePunkt = (kategorieIndex, punktIndex) => {
    if (window.confirm('Prüfpunkt wirklich löschen?')) {
      const updated = { ...selectedVorlage };
      updated.pruefpunkte[kategorieIndex].punkte.splice(punktIndex, 1);
      setSelectedVorlage(updated);
    }
  };

  const handleUpdatePunkt = (kategorieIndex, punktIndex, field, value) => {
    const updated = { ...selectedVorlage };
    updated.pruefpunkte[kategorieIndex].punkte[punktIndex][field] = value;
    setSelectedVorlage(updated);
  };

  const handleAddKategorie = () => {
    const newKategorie = {
      kategorie: 'Neue Kategorie',
      punkte: []
    };
    const updated = { ...selectedVorlage };
    updated.pruefpunkte.push(newKategorie);
    setSelectedVorlage(updated);
  };

  const handleRemoveKategorie = (kategorieIndex) => {
    if (window.confirm('Kategorie mit allen Prüfpunkten löschen?')) {
      const updated = { ...selectedVorlage };
      updated.pruefpunkte.splice(kategorieIndex, 1);
      setSelectedVorlage(updated);
    }
  };

  const handleDeleteVorlage = async (vorlage) => {
    if (!window.confirm(`Vorlage "${vorlage.anlagentyp}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/vorlagen/${vorlage.id}`, { headers: getAuthHeaders() });
      if (selectedVorlage?.id === vorlage.id) setSelectedVorlage(null);
      alert('Vorlage gelöscht');
      loadVorlagen();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Vorlage');
    }
  };

  const handleOpenKopieDialog = (vorlage) => {
    setKopieData({
      name: vorlage.name + ' (Kopie)',
      anlagentyp: vorlage.anlagentyp + ' (Kopie)',
      beschreibung: vorlage.beschreibung || ''
    });
    setSelectedVorlageForCopy(vorlage);
    setShowKopieDialog(true);
  };

  const handleKopieren = async () => {
    if (!kopieData.anlagentyp.trim()) {
      alert('Bitte geben Sie einen Anlagentyp ein');
      return;
    }
    try {
      await axios.post(
        `${API}/vorlagen/${selectedVorlageForCopy.id}/kopieren`,
        kopieData,
        { headers: getAuthHeaders() }
      );
      setShowKopieDialog(false);
      setKopieData({ name: '', anlagentyp: '', beschreibung: '' });
      setSelectedVorlageForCopy(null);
      alert('Vorlage erfolgreich kopiert');
      loadVorlagen();
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      alert('Fehler beim Kopieren der Vorlage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8" />
            Prüfprotokoll-Verwaltung
          </h1>
          <p className="text-gray-600 mt-2">
            Bearbeiten Sie die Wartungsprotokoll-Vorlagen für alle Anlagentypen
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Vorlagen-Liste */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Anlagentypen</h2>
              {loading ? (
                <div>Laden...</div>
              ) : (
                <div className="space-y-2">
                  {vorlagen.map((vorlage) => (
                    <div key={vorlage.id} className="flex items-center gap-2">
                      <button
                        onClick={() => handleSelectVorlage(vorlage)}
                        data-testid={`vorlage-select-${vorlage.id}`}
                        className={`flex-1 text-left px-4 py-3 rounded-lg transition ${
                          selectedVorlage?.id === vorlage.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                      >
                        <div className="font-medium">{vorlage.anlagentyp}</div>
                        <div className="text-sm opacity-75">
                          {vorlage.pruefpunkte.length} Kategorien
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenKopieDialog(vorlage);
                        }}
                        data-testid={`vorlage-copy-${vorlage.id}`}
                        title="Vorlage kopieren"
                        className="p-2 rounded-lg bg-gray-100 hover:bg-blue-100 text-gray-600 hover:text-blue-600 transition"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVorlage(vorlage);
                        }}
                        data-testid={`vorlage-delete-${vorlage.id}`}
                        title="Vorlage löschen"
                        className="p-2 rounded-lg bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Vorlagen-Editor */}
          <div className="lg:col-span-2">
            {!selectedVorlage ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Wählen Sie eine Vorlage zum Bearbeiten</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedVorlage.anlagentyp}</h2>
                    <p className="text-sm text-gray-500">{selectedVorlage.beschreibung}</p>
                  </div>
                  <div className="flex gap-2">
                    {editMode ? (
                      <>
                        <button
                          onClick={handleSaveVorlage}
                          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                        >
                          <Save className="w-4 h-4" />
                          Speichern
                        </button>
                        <button
                          onClick={() => {
                            setEditMode(false);
                            handleSelectVorlage(vorlagen.find(v => v.id === selectedVorlage.id));
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Abbrechen
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditMode(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                        Bearbeiten
                      </button>
                    )}
                  </div>
                </div>

                {/* Kategorien und Prüfpunkte */}
                <div className="space-y-6">
                  {selectedVorlage.pruefpunkte.map((kategorie, katIndex) => (
                    <div key={katIndex} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">{kategorie.kategorie}</h3>
                        {editMode && (
                          <button
                            onClick={() => handleRemoveKategorie(katIndex)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Prüfpunkte */}
                      <div className="space-y-3">
                        {kategorie.punkte.map((punkt, punktIndex) => (
                          <div key={punktIndex} className="bg-gray-50 p-3 rounded">
                            {editMode ? (
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={punkt.name}
                                    onChange={(e) => handleUpdatePunkt(katIndex, punktIndex, 'name', e.target.value)}
                                    className="flex-1 px-3 py-1 border rounded text-sm"
                                    placeholder="Prüfpunkt-Name"
                                  />
                                  <button
                                    onClick={() => handleRemovePunkt(katIndex, punktIndex)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <select
                                    value={punkt.typ}
                                    onChange={(e) => handleUpdatePunkt(katIndex, punktIndex, 'typ', e.target.value)}
                                    className="px-2 py-1 border rounded text-sm"
                                  >
                                    {FELDTYPEN.map(t => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    value={punkt.einheit || ''}
                                    onChange={(e) => handleUpdatePunkt(katIndex, punktIndex, 'einheit', e.target.value)}
                                    className="px-2 py-1 border rounded text-sm"
                                    placeholder="Einheit (z.B. kg)"
                                  />
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={punkt.pflicht}
                                      onChange={(e) => handleUpdatePunkt(katIndex, punktIndex, 'pflicht', e.target.checked)}
                                    />
                                    <span className="text-sm">Pflicht</span>
                                  </label>
                                </div>
                                {punkt.typ === 'select' && (
                                  <input
                                    type="text"
                                    value={(punkt.optionen || []).join(', ')}
                                    onChange={(e) => handleUpdatePunkt(katIndex, punktIndex, 'optionen', e.target.value.split(',').map(s => s.trim()))}
                                    className="w-full px-2 py-1 border rounded text-sm"
                                    placeholder="Optionen (komma-getrennt)"
                                  />
                                )}
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium text-sm">
                                  {punkt.name}
                                  {punkt.pflicht && <span className="text-red-600 ml-1">*</span>}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Typ: {FELDTYPEN.find(t => t.value === punkt.typ)?.label}
                                  {punkt.einheit && ` | Einheit: ${punkt.einheit}`}
                                  {punkt.optionen && punkt.optionen.length > 0 && ` | Optionen: ${punkt.optionen.join(', ')}`}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {editMode && (
                        <button
                          onClick={() => handleAddPunkt(katIndex)}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition"
                        >
                          <Plus className="w-4 h-4" />
                          Prüfpunkt hinzufügen
                        </button>
                      )}
                    </div>
                  ))}

                  {editMode && (
                    <button
                      onClick={handleAddKategorie}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 transition"
                    >
                      <Plus className="w-5 h-5" />
                      Kategorie hinzufügen
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kopier-Dialog */}
      {showKopieDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="copy-dialog">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Vorlage kopieren</h3>
              <button
                onClick={() => {
                  setShowKopieDialog(false);
                  setSelectedVorlageForCopy(null);
                }}
                data-testid="copy-dialog-close"
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anlagentyp *
                </label>
                <input
                  type="text"
                  value={kopieData.anlagentyp}
                  onChange={(e) => setKopieData({ ...kopieData, anlagentyp: e.target.value })}
                  data-testid="copy-dialog-anlagentyp"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. Kälteanlage Typ B"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={kopieData.name}
                  onChange={(e) => setKopieData({ ...kopieData, name: e.target.value })}
                  data-testid="copy-dialog-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Vorlagenname"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={kopieData.beschreibung}
                  onChange={(e) => setKopieData({ ...kopieData, beschreibung: e.target.value })}
                  data-testid="copy-dialog-beschreibung"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Optionale Beschreibung"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleKopieren}
                data-testid="copy-dialog-submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Kopieren
              </button>
              <button
                onClick={() => {
                  setShowKopieDialog(false);
                  setSelectedVorlageForCopy(null);
                }}
                data-testid="copy-dialog-cancel"
                className="flex-1 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VorlagenVerwaltung;
